import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const SCRIPT_ID = 'aspired-tracker'
const SCRIPT_SRC = 'https://aspiredwebsites.com/static/js/aspired-tracker.js'
const CLIENT_ID = 'acd113fa-c2cc-414d-b2e3-83e65670a8d6'

// Prefixes for the authenticated app + auth flows. The tracker is suppressed
// on any path that equals one of these OR starts with `${prefix}/...`.
// Includes /reset-password (with its :token sub-route) and the bulk of the
// post-login surface — dashboard, brain, settings, integrations, /agents/<id>
// chat, admin pages, etc.
const INTERNAL_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/onboarding',
  '/dashboard',
  '/brain',
  '/documents',
  '/agents/',          // /agents/<id> chat — note trailing slash so /agents (list) stays public
  '/settings',
  '/integrations',
  '/content-center',
  '/social',
  '/notifications',
  '/results',
  '/white-glove',
  '/checkout',
  // NOTE: '/account' is intentionally NOT blocked — we DO record the account
  // pages (billing, agents, settings, seats) to see what users do there. Card
  // entry there is safe: AddPaymentMethodModal uses Stripe <PaymentElement>,
  // which renders card fields in a cross-origin iframe the recorder can't read.
  '/admin',
  '/accept-seat',
]

// Routes that visually live inside an otherwise-blocked prefix but are
// actually public marketing surfaces.
const PUBLIC_EXCEPTIONS = new Set<string>([])

function isInternalRoute(pathname: string): boolean {
  if (PUBLIC_EXCEPTIONS.has(pathname)) return false
  for (const p of INTERNAL_PREFIXES) {
    if (p.endsWith('/')) {
      // Trailing slash signals "subpath only" — e.g. `/agents/` matches
      // `/agents/quinn` but NOT `/agents` (the public AgentsList page).
      if (pathname.startsWith(p)) return true
    } else {
      // No trailing slash: match the exact path OR any `${p}/...` subpath,
      // with a slash boundary so `/dashboard` doesn't shadow `/dashboardish`.
      if (pathname === p || pathname.startsWith(p + '/')) return true
    }
  }
  return false
}

/**
 * Mounts the Aspired Websites tracker script on public marketing routes
 * (Landing, Blog, Pricing, public agent detail pages, etc.) PLUS the /account
 * section (so we can see how users navigate billing/settings/etc.), and removes
 * it on the rest of the authenticated app (dashboard, chat, admin) + auth flows.
 * Card entry under /account is Stripe <PaymentElement> (cross-origin iframe),
 * so card data is never in the DOM the recorder can read.
 *
 * Note: the underlying tracker may attach global event listeners on first
 * load that aren't cleaned up when the script tag is removed. Removing the
 * tag prevents the script from being re-executed on a hard nav, but a
 * follow-up SPA nav from marketing → internal could still see the tracker's
 * already-attached handlers fire. If stricter gating is needed, expose a
 * pause API on the tracker itself rather than gating the tag here.
 */
export default function AspiredTracker() {
  const { pathname } = useLocation()

  useEffect(() => {
    const internal = isInternalRoute(pathname)
    const existing = document.getElementById(SCRIPT_ID)

    if (internal) {
      if (existing) existing.remove()
      return
    }
    if (existing) return

    const s = document.createElement('script')
    s.id = SCRIPT_ID
    s.src = SCRIPT_SRC
    s.defer = true
    s.setAttribute('data-aspired-client', CLIENT_ID)
    document.head.appendChild(s)
  }, [pathname])

  return null
}
